import { supabase } from '../../shared/supabase/supabaseClient';
import type {
  ExpenseSettlementListRow,
  ExpenseSettlementMethod,
  ExpenseSettlementsListQuery,
  ExpenseSettlementsListResult,
} from '../domain/expenseTypes';

export function mapRemoteSettlementRow(raw: Record<string, unknown>): ExpenseSettlementListRow {
  const method = String(raw.method ?? 'other') as ExpenseSettlementMethod;
  const settlementStatus = raw.settlement_status === 'voided' ? 'voided' : 'active';
  const paymentStatus =
    raw.payment_status === 'paid' || raw.payment_status === 'partial'
      ? raw.payment_status
      : 'unpaid';

  return {
    settlementId: String(raw.settlement_id ?? ''),
    expenseId: String(raw.expense_id ?? ''),
    // La RPC devuelve el branch de la factura padre — lo usamos para scoping
    // estricto en lecturas de cache (un settlement sin sucursal se excluye).
    expenseBranchId:
      raw.branch_id != null
        ? String(raw.branch_id)
        : raw.expense_branch_id != null
          ? String(raw.expense_branch_id)
          : null,
    method,
    settlementAmount: Number(raw.settlement_amount ?? 0),
    paidAt: String(raw.paid_at ?? ''),
    settlementStatus,
    note: raw.note != null ? String(raw.note) : null,
    cashOutflowId: raw.cash_outflow_id != null ? String(raw.cash_outflow_id) : null,
    createdBy: raw.created_by != null ? String(raw.created_by) : null,
    voidedAt: raw.voided_at != null ? String(raw.voided_at) : null,
    voidedBy: raw.voided_by != null ? String(raw.voided_by) : null,
    voidReason: raw.void_reason != null ? String(raw.void_reason) : null,
    expenseDate: String(raw.expense_date ?? ''),
    expenseAmount: Number(raw.expense_amount ?? 0),
    paymentStatus,
    expenseDescription: raw.expense_description != null ? String(raw.expense_description) : null,
    receiptRef: raw.receipt_ref != null ? String(raw.receipt_ref) : null,
    categorySlug: String(raw.category_slug ?? ''),
    categoryName: String(raw.category_name ?? ''),
    supplierName: raw.supplier_name != null ? String(raw.supplier_name) : null,
  };
}

export async function listBranchSettlementsRemote(
  query: ExpenseSettlementsListQuery
): Promise<ExpenseSettlementsListResult> {
  const { data, error } = await supabase.schema('pos').rpc('list_branch_expense_settlements', {
    p_branch_id: query.branchId,
    p_from: query.fromIso,
    p_to: query.toIso,
    p_method: query.method ?? null,
    p_status: query.status ?? 'active',
    p_limit: query.limit ?? 50,
    p_offset: query.offset ?? 0,
  });

  if (error) {
    console.warn('[listBranchSettlementsRemote]', error.message);
    throw new Error(error.message);
  }
  if (data == null) {
    throw new Error('El servidor no contestó.');
  }

  const payload = data as { rows?: unknown; total?: unknown };
  const rowsRaw = Array.isArray(payload.rows) ? payload.rows : [];
  return {
    rows: rowsRaw.map((row) => mapRemoteSettlementRow(row as Record<string, unknown>)),
    total: Number(payload.total ?? rowsRaw.length),
  };
}
